<template>
  <vs-popup
    title="Add card"
    :active.sync="isVisible"
    class="add-card-popup"
    :class="{'disabled': isConfirmCardSetupLoading}"
  >
    <div v-if="isInitPaymentLoading" class="text-center">Wait for init payment...</div>
    <div v-else-if="initPaymentError" class="text-center">
      <p class="text-error">{{ initPaymentError }}</p>
      <vs-button color="primary" @click="handleInitPayment">Retry</vs-button>
    </div>
    <div v-else-if="isInitCardElementLoading" class="text-center">Wait for create card element...</div>
    <div v-else-if="initCardElementError" class="text-center">
      <p class="text-error">{{ initCardElementError }}</p>
      <vs-button color="primary" @click="handleInitCardElement">Retry</vs-button>
    </div>
    <div v-show="!isInitPaymentLoading && !initPaymentError && !isInitCardElementLoading && !initCardElementError"
         class="stripe-card">
      <div class="stripe-card__row">
        <label class="stripe-card__label">Card Number</label>
        <div id="card-number" class="stripe-card__input"></div>
      </div>
      <div class="stripe-card__row">
        <label class="stripe-card__label">Card Expiry</label>
        <div id="card-expiry" class="stripe-card__input"></div>
      </div>
      <div class="stripe-card__row">
        <label class="stripe-card__label">Card CVC</label>
        <div id="card-cvc" class="stripe-card__input"></div>
      </div>
      <div class="stripe-card__row" v-if="confirmCardSetupError">
        <div class="stripe-card__error">{{ confirmCardSetupError }}</div>
      </div>
      <div class="stripe-card__row">
        <vs-button class="w-full" color="primary" @click="handleConfirmCardSetup" :disabled="isConfirmCardSetupLoading">
          {{ isConfirmCardSetupLoading ? 'Adding...' : 'Add' }}
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import Vue from 'vue';
import {StripePlugin} from '@vue-stripe/vue-stripe';

export default {
  data() {
    return {
      isVisible: false,
      isInitCardElementLoading: false,
      initCardElementError: null,
      isConfirmCardSetupLoading: false,
      confirmCardSetupError: null,
      paymentInitialize: null,
      isInitPaymentLoading: false,
      initPaymentError: null,
    }
  },
  methods: {
    handleResetState() {
      this.cardNumber && this.cardNumber.clear();
      this.cardExpiry && this.cardExpiry.clear();
      this.cardCvc && this.cardCvc.clear();
      this.confirmCardSetupError = null;
      this.initPaymentError = null;
    },
    handleInitCardElement() {
      try {
        this.isInitCardElementLoading = true;
        this.initCardElementError = null;
        const style = {
          base: {
            fontSize: '16px',
            '::placeholder': {
              color: '#9a9a9a',
            },
          },
        };
        this.cardNumber = this.stripeElements.create('cardNumber', {style});
        this.cardNumber.mount('#card-number');
        this.cardExpiry = this.stripeElements.create('cardExpiry', {style});
        this.cardExpiry.mount('#card-expiry');
        this.cardCvc = this.stripeElements.create('cardCvc', {style});
        this.cardCvc.mount('#card-cvc');
      } catch (error) {
        this.initCardElementError = `Can't create stripe card elements: ${error.message}`
      } finally {
        this.isInitCardElementLoading = false;
      }
    },
    async handleInitPayment() {
      try {
        this.initPaymentError = null;
        this.isInitPaymentLoading = true;
        this.paymentInitialize = await this.$store.dispatch("appointment/INITIALIZE_PAYMENT", {
          appointment_id: this.appointmentDetail.id,
        });
      } catch (error) {
        console.error(error);
        this.initPaymentError = `Initialize failed: ${error.message}`;
      } finally {
        this.isInitPaymentLoading = false;
      }
    },
    handleAddStripePaymentMethod() {
      return new Promise(async (resolve, reject) => {
        try {
          await this.$store.dispatch("appointment/ADD_STRIPE_PAYMENT_METHOD", {
            appointment_id: this.appointmentDetail.id,
            external_payment_id: this.paymentInitialize.payment_intent_id,
          });
          resolve(true);
        } catch (error) {
          reject(error);
        }
      })
    },
    async handleConfirmCardSetup() {
      try {
        this.confirmCardSetupError = null;
        this.isConfirmCardSetupLoading = true;
        const {setupIntent, error} = await this.$stripe.confirmCardSetup(
          this.paymentInitialize.client_secret,
          {
            payment_method: {
              card: this.cardNumber
            }
          }
        );
        if (error) {
          throw error;
        }
        await this.handleAddStripePaymentMethod();
        this.$vs.notify({
          title: 'Add card success',
          color: "success",
          position: "top-right"
        });
        this.$emit('success');
        this.hide();
      } catch (error) {
        console.error(error);
        if (error.message.message) {
          return this.confirmCardSetupError = error.message.message;
        }
        this.confirmCardSetupError = error.message;
      } finally {
        this.isConfirmCardSetupLoading = false;
      }
    },
    show() {
      this.handleResetState();
      this.handleInitPayment();
      this.isVisible = true;
    },
    hide() {
      this.isVisible = false;
    }
  },
  computed: {
    config() {
      return this.$store.getters["auth/config"];
    },
    appointmentDetail() {
      return this.$store.getters["appointment/appointmentDetail"];
    },
    stripeElements() {
      return this.$stripe.elements();
    },
  },
  async created() {
    Vue.use(StripePlugin, {
      pk: this.config.stripe_publish_key,
    });
  },
  mounted() {
    this.handleInitCardElement();
  },
  beforeDestroy() {
    this.cardNumber && this.cardNumber.destroy();
    this.cardExpiry && this.cardExpiry.destroy();
    this.cardCvc && this.cardCvc.destroy();
  },
}
</script>

<style lang="scss">
.add-card-popup {
  z-index: 53001;

  &.disabled {
    pointer-events: none;
  }
}

.stripe-card {
  $color-primary: #5E73FF;
  $color-error: #ff5252;
  $color-bg: #F6F6F6;
  $color-white: #fff;
  $border-radius: 4px;
  $padding: 10px 16px;
  $min-height: 42px;
  width: 100%;

  &--disabled {
    opacity: 0.85;
    pointer-events: none;
  }

  &__row + &__row {
    margin-top: 12px;
  }

  &__label {
    font-weight: 500;
    margin-bottom: 6px;
  }

  &__input {
    background-color: $color-bg;
    border: 1px solid $color-bg;
    height: auto;
    min-height: $min-height;
    border-radius: $border-radius;
    padding: $padding;
    box-shadow: none;
    transition: 0.2s ease;

    &.StripeElement--focus, &:focus-within {
      box-shadow: none;
      border-color: $color-primary;
    }

    &.StripeElement--error,
    &.StripeElement--invalid {
      &, &.StripeElement--focus {
        border-color: $color-error;
      }
    }
  }

  &__error {
    color: $color-error;
    font-size: 12px;
    //border: 2px solid $color-error;
    //padding: $padding;
    //border-radius: $border-radius;
  }

  display: flex;
  flex-wrap: wrap;

  &__row {
    width: 100%;

    &:nth-child(2) {
      width: 70%;
    }

    &:nth-child(3) {
      width: calc(30% - 12px);
      margin-left: 12px;
    }
  }

  .vs-btn {
    border-radius: $border-radius;
    padding: $padding !important;
    height: auto !important;
    width: 100%;
    min-height: $min-height;
  }
}
</style>
